"use client";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules } from "../components/FormValidation";
import { cn } from "@/lib/utils";
import parse from "html-react-parser";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";

type CriteriaWithSelectionStepProps<T = BaseFormData> = StepProps<T> & {
  selectionQuestion?: string;
  selectionOptions?: Array<{
    value: string;
    title: string;
    description: string;
  }>;
  infoBoxText?: string;
};

export const CriteriaWithSelectionStep = <T extends BaseFormData = BaseFormData>({ 
  formData, 
  title, 
  description, 
  selectionQuestion = "Does your business meet the below criteria?",
  selectionOptions = [],
  infoBoxText,
}: CriteriaWithSelectionStepProps<T>) => {
  const { control } = useFormContext();

  return (
    <div className="flex flex-col gap-12">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <h3>{title}</h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground [&_strong]:text-foreground">
            {parse(description)}
          </p>
        )}
      </div>

      {/* Selection Question */}
      <div className="flex flex-col gap-6">
        <p className="paragraph-base text-foreground font-bold">
          {parse(selectionQuestion)}
        </p>
        
        <FormField
          control={control}
          name={"criteriaSelection" as never}
          rules={ValidationRules.selection("selection")}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <RadioGroup 
                  value={(field.value as string) ?? (formData.criteriaSelection as string) ?? ""}
                  onValueChange={(value) => { field.onChange(value); }}
                  className="border border-border rounded-3xl gap-0"
                >
                  {selectionOptions?.map((option: { value: string; title: string; description: string }, index: number) => (
                    <Label
                      htmlFor={option.value}
                      key={option.value}
                      className={cn(
                        "w-full px-6 py-4 flex items-center gap-6 cursor-pointer",
                        index === 0 && "rounded-t-3xl",
                        index === selectionOptions.length - 1 && "rounded-b-3xl",
                        index < selectionOptions.length - 1 && "border-b border-border"
                      )}
                    >
                      <RadioGroupItem value={option.value} id={option.value} className="cursor-pointer" />
                      <div className="flex-1">
                        <div className="flex flex-col gap-1 cursor-pointer items-start">
                          <p className="paragraph-base text-foreground font-bold">{option.title}</p>
                          <p className="paragraph-base text-muted-foreground font-normal">
                            {parse(option.description)}
                          </p>
                        </div>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Information Box */}
      {infoBoxText && (
        <div className="bg-muted/50 p-4 rounded-lg">
          <p className="paragraph-sm text-muted-foreground [&_strong]:text-foreground">
            {parse(infoBoxText)}
          </p>
        </div>
      )}

    </div>
  );
};
